import React, { useState, useEffect, useRef, useCallback } from "react";
import { Search, ChevronDown, Clock, Loader2, X } from "lucide-react";
import { Button } from "./Button";
import { Input } from "./input";
import { useAppDispatch, useAppSelector } from "../../redux/store";
import { getAllTimezones } from "../../redux/Actions/AuthAction";

interface Timezone {
    id: string;
    name: string;
    value: string;
    label: string;
}

interface TimezoneDropdownProps {
    selectedTimezone?: Timezone | null;
    selectedTimezones?: Timezone[];
    onTimezoneSelect?: (timezone: Timezone | null) => void;
    onTimezonesSelect?: (timezones: Timezone[]) => void;
    placeholder?: string;
    className?: string;
    disabled?: boolean;
    autoDetect?: boolean; // New prop to enable auto-detection
    multiple?: boolean; // New prop to enable multiple selection
}

const TimezoneDropdown: React.FC<TimezoneDropdownProps> = ({
    selectedTimezone,
    selectedTimezones = [],
    onTimezoneSelect,
    onTimezonesSelect,
    placeholder = "Select a timezone...",
    className = "",
    disabled = false,
    autoDetect = false,
    multiple = false,
}) => {
    const dispatch = useAppDispatch();
    const { loading } = useAppSelector((state: any) => state.auth || {
        loading: false,
    });

    const [isOpen, setIsOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(null);
    const [isDetectingTimezone, setIsDetectingTimezone] = useState(false);
    const [internalSelectedTimezone, setInternalSelectedTimezone] = useState<Timezone | null>(null);
    const [internalSelectedTimezones, setInternalSelectedTimezones] = useState<Timezone[]>([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [timezones, setTimezones] = useState<Timezone[]>([]);
    const [isInitialLoading, setIsInitialLoading] = useState(true);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const listRef = useRef<HTMLDivElement>(null);
    const loadMoreRef = useRef<() => void>();

    // Load timezones on component mount
    useEffect(() => {
        setIsInitialLoading(true);
        dispatch(getAllTimezones({
            page: 1,
            limit: 20,
            isShowLoader: false,
        })).then((action: any) => {
            if (action.payload && action.payload.data) {
                const timezoneData = action.payload.data.map((item: any) => ({
                    id: item.id,
                    name: item.name,
                    value: item.id,
                    label: item.name,
                }));
                setTimezones(timezoneData);
                setHasMore(timezoneData.length === 20);
            }
            setIsInitialLoading(false);
        }).catch(() => {
            setIsInitialLoading(false);
        });
    }, [dispatch]);

    // Reset pagination when search query changes
    useEffect(() => {
        if (searchQuery !== "") {
            setCurrentPage(1);
            setHasMore(true);
            setTimezones([]);
        }
    }, [searchQuery]);

    // Timezone detection and caching (only if autoDetect is enabled)
    useEffect(() => {
        if (autoDetect && timezones && timezones.length > 0 && !internalSelectedTimezone) {
            setIsDetectingTimezone(true);
            try {
                // Try to detect timezone from browser
                const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
                const detectedTimezone = timezones.find(tz => tz.value === browserTimezone || tz.name === browserTimezone);
                
                if (detectedTimezone) {
                    setInternalSelectedTimezone(detectedTimezone);
                    onTimezoneSelect?.(detectedTimezone);
                    console.log("Auto-detected timezone:", detectedTimezone.label);
                } else {
                    // Fallback to first timezone if detection fails
                    const defaultTimezone = timezones[0];
                    if (defaultTimezone) {
                        setInternalSelectedTimezone(defaultTimezone);
                        onTimezoneSelect?.(defaultTimezone);
                        console.log("Set default timezone:", defaultTimezone.label);
                    }
                }
            } catch (error) {
                console.error("Error detecting timezone:", error);
            } finally {
                setIsDetectingTimezone(false);
            }
        }
    }, [autoDetect, timezones.length, onTimezoneSelect, internalSelectedTimezone]);

    // Sync with external props
    useEffect(() => {
        if (multiple && selectedTimezones !== undefined) {
            setInternalSelectedTimezones(selectedTimezones);
        } else if (!multiple && selectedTimezone !== undefined) {
            setInternalSelectedTimezone(selectedTimezone);
        }
    }, [selectedTimezone?.id, selectedTimezones, multiple]);

    // Handle search with debounce
    const handleSearch = useCallback((query: string) => {
        if (searchTimeout) {
            clearTimeout(searchTimeout);
        }

        const timeout = setTimeout(() => {
            setCurrentPage(1);
            setHasMore(true);
            setTimezones([]);
            setIsInitialLoading(true);
            
            dispatch(getAllTimezones({
                page: 1,
                limit: 20,
                search: query,
                isShowLoader: false, // Disable global loader to prevent form resets
            })).then((action: any) => {
                if (action.payload && action.payload.data) {
                    const timezoneData = action.payload.data.map((item: any) => ({
                        id: item.id,
                        name: item.name,
                        value: item.id,
                        label: item.name,
                    }));
                    setTimezones(timezoneData);
                    setHasMore(timezoneData.length === 20);
                }
                setIsInitialLoading(false);
            }).catch(() => {
                setIsInitialLoading(false);
            });
        }, 300);

        setSearchTimeout(timeout);
    }, [dispatch]);

    // Load more timezones
    const loadMoreTimezones = useCallback(() => {
        if (isLoadingMore || !hasMore) return;
        
        setIsLoadingMore(true);
        const nextPage = currentPage + 1;
        
        dispatch(getAllTimezones({
            page: nextPage,
            limit: 20,
            search: searchQuery,
            isShowLoader: false,
        })).then((action: any) => {
            if (action.payload && action.payload.data) {
                const newTimezones = action.payload.data.map((item: any) => ({
                    id: item.id,
                    name: item.name,
                    value: item.id,
                    label: item.name,
                }));
                
                setTimezones(prev => [...prev, ...newTimezones]);
                
                if (newTimezones.length < 20) {
                    setHasMore(false);
                }
                setCurrentPage(nextPage);
            } else {
                setHasMore(false);
            }
        }).catch((error) => {
            console.error("Error loading more timezones:", error);
            setHasMore(false);
        }).finally(() => {
            setIsLoadingMore(false);
        });
    }, [dispatch, currentPage, hasMore, isLoadingMore, searchQuery]);

    // Update the ref whenever loadMoreTimezones changes
    useEffect(() => {
        loadMoreRef.current = loadMoreTimezones;
    }, [loadMoreTimezones]);

    // Handle scroll for infinite loading
    const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
        const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
        const isNearBottom = scrollTop + clientHeight >= scrollHeight - 10;
        
        if (isNearBottom && hasMore && !isLoadingMore && loadMoreRef.current) {
            loadMoreRef.current();
        }
    }, [hasMore, isLoadingMore]);

    // Handle timezone selection
    const handleTimezoneSelect = useCallback((timezone: Timezone) => {
        if (multiple) {
            const isSelected = internalSelectedTimezones.some(t => t.id === timezone.id);
            let newSelectedTimezones;
            
            if (isSelected) {
                // Remove timezone if already selected
                newSelectedTimezones = internalSelectedTimezones.filter(t => t.id !== timezone.id);
            } else {
                // Add timezone if not selected
                newSelectedTimezones = [...internalSelectedTimezones, timezone];
            }
            
            setInternalSelectedTimezones(newSelectedTimezones);
            onTimezonesSelect?.(newSelectedTimezones);
            
            // Don't close dropdown in multiple mode
        } else {
            setInternalSelectedTimezone(timezone);
            onTimezoneSelect?.(timezone);
            setIsOpen(false);
            setSearchQuery("");
            
            console.log("Timezone selected:", timezone.label);
        }
    }, [multiple, internalSelectedTimezones, onTimezonesSelect, onTimezoneSelect]);

    // Handle clear selection
    const handleClear = useCallback((e: React.MouseEvent) => {
        e.stopPropagation();
        if (multiple) {
            setInternalSelectedTimezones([]);
            onTimezonesSelect?.([]);
        } else {
            setInternalSelectedTimezone(null);
            onTimezoneSelect?.(null);
        }
    }, [multiple, onTimezonesSelect, onTimezoneSelect]);

    // Handle dropdown toggle
    const toggleDropdown = useCallback(() => {
        if (disabled) return;
        setIsOpen(!isOpen);
        if (!isOpen) {
            // Focus search input when opening
            setTimeout(() => {
                searchInputRef.current?.focus();
            }, 100);
        }
    }, [disabled, isOpen]);

    // Handle click outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (searchTimeout) {
                clearTimeout(searchTimeout);
            }
        };
    }, [searchTimeout]);

    return (
        <div className={`relative ${className}`} ref={dropdownRef}>
            {/* Trigger Button */}
            <button
                type="button"
                onClick={toggleDropdown}
                disabled={disabled || isDetectingTimezone}
                className={`
					w-full flex items-center justify-between px-3 py-2 border rounded-lg
					${disabled || isDetectingTimezone
                        ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                        : "bg-white hover:border-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    }
					transition-colors
				`}
            >
                <div className="flex items-center gap-2 flex-1 min-w-0">
                    {isDetectingTimezone ? (
                        <>
                            <Loader2 className="h-4 w-4 animate-spin text-blue-600 flex-shrink-0" />
                            <span className="text-gray-500">Detecting timezone...</span>
                        </>
                    ) : multiple ? (
                        internalSelectedTimezones.length > 0 ? (
                            <div className="flex items-center gap-1 flex-wrap">
                                <Clock className="h-4 w-4 text-gray-500 flex-shrink-0" />
                                {internalSelectedTimezones.length <= 2 ? (
                                    internalSelectedTimezones.map(timezone => (
                                        <span key={timezone.id} className="text-sm bg-blue-100 text-blue-800 px-2 py-1 rounded">
                                            {timezone.label}
                                        </span>
                                    ))
                                ) : (
                                    <>
                                        <span className="text-sm bg-blue-100 text-blue-800 px-2 py-1 rounded">
                                            {internalSelectedTimezones[0].label}
                                        </span>
                                        <span className="text-sm text-gray-500">
                                            +{internalSelectedTimezones.length - 1} more
                                        </span>
                                    </>
                                )}
                            </div>
                        ) : (
                            <span className="text-gray-500">{placeholder}</span>
                        )
                    ) : internalSelectedTimezone ? (
                        <>
                            <Clock className="h-4 w-4 text-gray-500 flex-shrink-0" />
                            <span className="truncate">{internalSelectedTimezone.label}</span>
                            <span className="text-gray-400 text-sm">({internalSelectedTimezone.value})</span>
                        </>
                    ) : (
                        <span className="text-gray-500">{placeholder}</span>
                    )}
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                    {((multiple && internalSelectedTimezones.length > 0) || (!multiple && internalSelectedTimezone)) && !isDetectingTimezone && (
                        <button
                            type="button"
                            onClick={handleClear}
                            className="p-1 hover:bg-gray-200 rounded-full transition-colors"
                        >
                            <X className="h-3 w-3 text-gray-400" />
                        </button>
                    )}
                    <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                </div>
            </button>

            {/* Dropdown Menu */}
            {isOpen && (
                <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-80 overflow-hidden">
                    {/* Search Input */}
                    <div className="p-3 border-b border-gray-200">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                            <Input
                                ref={searchInputRef}
                                type="text"
                                placeholder="Search timezones..."
                                value={searchQuery}
                                onChange={(e) => {
                                    setSearchQuery(e.target.value);
                                    handleSearch(e.target.value);
                                }}
                                className="pl-10"
                            />
                        </div>
                    </div>

                    {/* Timezones List */}
                    <div 
                        ref={listRef}
                        className="max-h-60 overflow-y-auto"
                        onScroll={handleScroll}
                    >
                        {isInitialLoading && timezones.length === 0 ? (
                            <div className="flex items-center justify-center py-8">
                                <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                                <span className="ml-2 text-gray-500">Loading timezones...</span>
                            </div>
                        ) : timezones.length === 0 ? (
                            <div className="flex flex-col items-center justify-center py-8 text-gray-500">
                                <Clock className="h-8 w-8 text-gray-300 mb-2" />
                                <p>No timezones found</p>
                                {searchQuery && (
                                    <p className="text-sm text-gray-400">Try a different search term</p>
                                )}
                            </div>
                        ) : (
                            <>
                                {timezones.map((timezone: Timezone) => {
                                    const isSelected = multiple 
                                        ? internalSelectedTimezones.some(t => t.id === timezone.id)
                                        : internalSelectedTimezone?.id === timezone.id;
                                    
                                    return (
                                        <button
                                            key={timezone.id}
                                            type="button"
                                            onClick={() => handleTimezoneSelect(timezone)}
                                            className={`
                                                w-full flex items-center gap-3 px-3 py-3 text-left hover:bg-gray-50
                                                ${isSelected ? "bg-blue-50 border-l-4 border-blue-500" : ""}
                                                transition-colors
                                            `}
                                        >
                                            <div className="flex-shrink-0">
                                                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                                                    isSelected ? "bg-blue-100" : "bg-gray-200"
                                                }`}>
                                                    <Clock className={`h-4 w-4 ${
                                                        isSelected ? "text-blue-600" : "text-gray-500"
                                                    }`} />
                                                </div>
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className={`font-medium truncate ${
                                                    isSelected ? "text-blue-900" : "text-gray-900"
                                                }`}>
                                                    {timezone.label}
                                                </p>
                                                <p className={`text-sm truncate ${
                                                    isSelected ? "text-blue-600" : "text-gray-500"
                                                }`}>{timezone.value}</p>
                                            </div>
                                            {multiple && isSelected && (
                                                <div className="flex-shrink-0">
                                                    <div className="w-5 h-5 bg-blue-600 rounded-full flex items-center justify-center">
                                                        <span className="text-white text-xs">✓</span>
                                                    </div>
                                                </div>
                                            )}
                                        </button>
                                    );
                                })}
                                
                                {/* Load More Button */}
                                {hasMore && (
                                    <div className="p-3 border-t border-gray-200">
                                        <button
                                            type="button"
                                            onClick={loadMoreTimezones}
                                            disabled={isLoadingMore}
                                            className="w-full flex items-center justify-center gap-2 px-4 py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            {isLoadingMore ? (
                                                <>
                                                    <Loader2 className="h-4 w-4 animate-spin" />
                                                    <span>Loading more...</span>
                                                </>
                                            ) : (
                                                <span>Load more timezones</span>
                                            )}
                                        </button>
                                    </div>
                                )}
                                
                                {/* End of list indicator */}
                                {!hasMore && timezones.length > 0 && (
                                    <div className="p-3 text-center text-xs text-gray-400 border-t border-gray-100">
                                        All timezones loaded
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default TimezoneDropdown;
